"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const axios_1 = __importDefault(require("axios"));
const difference_1 = __importDefault(require("lodash/fp/difference"));
const LoggerConfig = {
    baseUrl: "/v3/api/log",
    logLevel: process.env.CLIENT_LOG_LEVEL,
    sendInterval: 1000,
};
/**
 * The logger largely mimics the console api and emits to the server based on a config
 *
 * This is it's overall pattern:
 * 1. Get constructed.
 * 2. Collect logs while waiting N
 * 3. Fire logs to server, get results
 * [repeat steps 2 - 3 ad infinitum]
 */
class Logger {
    /**
     * Make a new logger. This also fires off the log collection logic.
     * @param {object} options - The configuration parameters for the logger
     * @param {string} options.baseUrl - The domain and path to be using to log
     * @param {number} options.sendInterval - How frequently to send logs to the server
     */
    constructor() {
        /**
         * Variadic function taking an optional level and a series of followup values
         * Everything gets converted safely into a string with JSON.stringify()
         * @param {any} level - Optional flag matching a level
         * @param  {any[]} values
         */
        this.logs = [];
        this.log = (_level = "info", ...values) => {
            const hasLevel = Logger.levels.includes(_level);
            const level = hasLevel ? _level : "info";
            const messageArray = hasLevel ? values : [_level].concat(values);
            const message = messageArray
                .map((value) => {
                if (value instanceof Error) {
                    return value.stack;
                }
                return JSON.stringify(value);
            })
                .join(" ");
            const timestamp = Date.now();
            this.logs.push({
                level,
                message,
                timestamp,
            });
        };
        /**
         * Starting the business logic
         */
        this.collectLogs();
    }
    /**
     * Internal method, simply waits and then sends
     */
    collectLogs() {
        setTimeout(() => this.emitLogs(), LoggerConfig.sendInterval);
    }
    /**
     * Internal method, takes our logs and posts them to the server.
     * If it is successful, we remove all sent logs from this.logs.
     * We don't fully empty in case more logs came in
     * while we were waiting for our results
     */
    async emitLogs() {
        const { logs } = this;
        const _logs = logs.slice(0);
        if (_logs.length) {
            try {
                await axios_1.default.post(LoggerConfig.baseUrl, { logs: _logs }, { withCredentials: true });
                this.logs = (0, difference_1.default)(this.logs, _logs);
            }
            catch (err) {
                console.error("Failed to report logs", ..._logs);
            }
        }
        this.collectLogs();
    }
}
/**
 * The range of standard log levels
 */
Logger.levels = ["error", "warn", "info", "debug"];
/**
 * Turn all of the log levels into prototype functions that wrap log
 */
Logger.levels.forEach((level) => {
    Logger.prototype[level] = logAtLevel;
    function logAtLevel(...values) {
        return this.log(level, ...values);
    }
});
/**
 * Instantiate the logger as a singleton.
 * We want to export one instance as a singleton
 * so we can tinker with window internals and not worry about
 * losing a reference to our chicanery.
 */
const logger = new Logger();
/**
 * Override the console functions to make it use our stuff
 */
if (process.env.CAPTURE_CONSOLE) {
    const console = window.console;
    Logger.levels.forEach((level) => {
        const originalConsole = console[level];
        console[level] = overrideConsole;
        function overrideConsole(...args) {
            logger[level](...args);
            originalConsole(...args);
        }
    });
    const consoleLog = console.log;
    console.log = (...args) => {
        consoleLog(...args);
        logger.log(...args);
    };
}
exports.default = logger;
